import React from "react";
import { useTranslation } from "react-i18next";
import { coinsForCents, dollarsForCoins, formatDollars } from "@/lib/coins";
import { coinVars } from "@/lib/coins";

// Canonical price presenter: coin amount is the big number and the derived dollar
// value sits to its RIGHT on the same line, in parentheses. The dollar is small but
// bold (matching the coin weight), never faint, and is ALWAYS rendered (legal
// requirement: a wallet payment never reaches a Stripe screen, so the page must
// carry the real money price). Pass either `coins` or `cents`; dollars are derived.
export const CoinPrice = ({ coins, cents, size = "md", align = "start", strike = false, tone = "auto", stacked = false, className = "", testid }) => {
  const { t } = useTranslation();
  const c = coins != null ? Number(coins) : coinsForCents(cents);
  // When cents is given it is the exact amount charged, so show that true dollar.
  // The coin count is the ceil approximation. When only coins is given, derive it.
  const dollars = cents != null ? Number(cents) / 100 : dollarsForCoins(c);
  const sizes = {
    xs: { coin: "text-sm font-bold leading-tight", dollar: "text-[10px] font-bold" },
    sm: { coin: "text-base font-bold leading-tight", dollar: "text-xs font-bold" },
    md: { coin: "text-2xl font-extrabold leading-tight", dollar: "text-sm font-extrabold" },
    lg: { coin: "text-4xl sm:text-5xl font-extrabold leading-tight", dollar: "text-lg font-extrabold" },
  }[size] || { coin: "text-2xl font-extrabold leading-tight", dollar: "text-sm font-extrabold" };
  const justifyCls = align === "center" ? "justify-center" : "justify-start";
  const itemsCls = align === "center" ? "items-center" : "items-start";
  const strikeCls = strike ? "line-through" : "";
  // Opt-in only: stacked drops the dollar onto its own line BELOW the coins (used by the
  // Buy Coins pack tiles). Default stays the canonical inline-in-parentheses layout, so
  // no other price on the platform changes.
  const layoutCls = stacked
    ? `inline-flex flex-col ${itemsCls} gap-0.5`
    : `inline-flex items-baseline gap-1.5 whitespace-nowrap ${justifyCls}`;
  // Compare-at (struck) uses the muted token; the live price uses the primary token.
  // tone="inherit" drops the explicit colour so the price follows its parent (used on
  // fixed-background buttons). No hardcoded colour otherwise, so both modes read right.
  const color = tone === "inherit" ? null : strike ? "var(--text-secondary)" : "var(--text-primary)";
  return (
    <span className={`${layoutCls} ${strikeCls} ${className}`} style={color ? { color } : undefined} data-testid={testid || "coin-price"}>
      <span className={sizes.coin} data-testid="coin-price-coins">{t("coins.perPrice", coinVars(c))}</span>
      {/* Struck compare-at shows coins only: a struck dollar beside the live dollar is
          noise, and the struck figure is the price the user does not pay. Stacked shows
          the dollar on its own line without parentheses. */}
      {!strike && <span className={sizes.dollar} data-testid="coin-price-dollars">{stacked ? formatDollars(dollars) : `(${formatDollars(dollars)})`}</span>}
    </span>
  );
};

// Sentinel marking where the shared CoinPrice should be spliced into a copy
// template. Callers interpolate {{price}} with this value (via t or a string
// replace) so word order stays locale-controlled and CMS copy keeps working.
export const COIN_PRICE_SLOT = "\u0001";

// Renders a copy string containing COIN_PRICE_SLOT, dropping the shared CoinPrice
// (coins big, dollar in parens, bold) in place of the slot. A template without the
// slot (for example a locale not migrated yet) renders as plain text, so a raw
// token is never shown.
export const CopyWithCoinPrice = ({ template, coins, cents, size = "sm", testid }) => {
  if (typeof template !== "string") return null;
  const parts = template.split(COIN_PRICE_SLOT);
  if (parts.length === 1) return <>{template}</>;
  return (
    <>
      {parts.map((seg, i) => (
        <React.Fragment key={i}>
          {seg}
          {i < parts.length - 1 && <CoinPrice coins={coins} cents={cents} size={size} testid={testid} />}
        </React.Fragment>
      ))}
    </>
  );
};

export default CoinPrice;
